import { useCallback, useEffect } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';

import type { ContactZConnection } from '../types';

export type ContactZConnectionObserverEvent = Partial<ContactZConnection> & {
  id: string;
};

interface UseContactZConnectionsWebSocketOptions {
  enabled?: boolean;
  onEvent?: (event: ContactZConnectionObserverEvent, action: string) => void;
}

/**
 * Subscribe to tenant-scoped Contact Z connection updates over the shared websocket.
 */
export function useContactZConnectionsWebSocket(
  options: UseContactZConnectionsWebSocketOptions = {},
) {
  const { onEvent, enabled = true } = options;
  const { currentTenant, user } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();

  const tenantId = currentTenant?.id || user?.tenantId || null;

  const handleEvent = useCallback(
    (data: ContactZConnectionObserverEvent, action: string) => {
      if (action !== 'update' && action !== 'create' && action !== 'delete') {
        return;
      }

      const normalized = transformKeys<ContactZConnectionObserverEvent>(data);
      onEvent?.(normalized, action);
    },
    [onEvent],
  );

  useEffect(() => {
    if (!enabled || !tenantId || !state.isConnected || !onEvent) {
      return;
    }

    const observerId = addObserver(
      'contact_z_connection',
      { data: { tenant_id: tenantId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
        includeUpdateEvents: true,
        includeDeleteEvents: true,
      },
    );

    return () => {
      removeObserver(observerId);
    };
  }, [enabled, tenantId, state.isConnected, addObserver, removeObserver, handleEvent, onEvent]);

  return {
    isConnected: Boolean(tenantId) && state.isConnected,
  };
}

export default useContactZConnectionsWebSocket;
